// =====================================================
// PAGE ADMIN: Formulaire Coupon (Création/Édition)
// Date: 2025-11-28
// Description: Création et modification de coupons
// =====================================================

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  ArrowLeft, 
  Save, 
  Tag, 
  Percent, 
  Calendar, 
  Users,
  ShoppingBag,
  BookOpen,
  Heart,
  Loader2,
  Info,
  AlertCircle
} from 'lucide-react';
import { AdminLayout } from '../../../components/layout/AdminLayout';
import { toast } from 'sonner';
import { useTenantContext } from '../../../context/TenantContext';
import { 
  getCouponById, 
  createCoupon, 
  updateCoupon,
  type Coupon,
  type ModuleType,
  type DiscountType
} from '../../../services/couponService';

const allModuleOptions: { value: ModuleType; label: string; icon: React.ElementType; color: string; configKey?: string }[] = [
  { value: 'shop', label: 'Boutique', icon: ShoppingBag, color: 'text-orange-400' }, // Toujours disponible
  { value: 'elearning', label: 'E-learning', icon: BookOpen, color: 'text-blue-400', configKey: 'elearning' },
  { value: 'psy', label: 'Consultations Psy', icon: Heart, color: 'text-purple-400', configKey: 'psy' },
  { value: 'dating', label: 'Rencontres', icon: Heart, color: 'text-pink-400', configKey: 'dating' }
];

const NEVER_EXPIRES_DATE = '2099-12-31';

export default function CouponForm() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditing = id && id !== 'new';
  const { tenant } = useTenantContext();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Filtrer les modules disponibles en fonction de la config du tenant
  const availableModules = useMemo(() => {
    const couponModulesConfig = tenant?.config?.coupon_modules || {};
    return allModuleOptions.filter(module => {
      // Shop est toujours disponible
      if (!module.configKey) return true;
      // Les autres modules dépendent de la config du tenant
      return couponModulesConfig[module.configKey] === true;
    });
  }, [tenant?.config?.coupon_modules]);

  // Modules par défaut = uniquement les modules disponibles
  const defaultModules = useMemo(() => {
    return availableModules.map(m => m.value);
  }, [availableModules]);

  const [formData, setFormData] = useState({
    code: '',
    name: '',
    description: '',
    discount_type: 'percentage' as DiscountType,
    discount_value: 0,
    min_purchase_amount: 0,
    max_discount_amount: null as number | null,
    usage_limit: null as number | null,
    usage_limit_per_user: 1,
    valid_from: new Date().toISOString().split('T')[0],
    valid_until: '',
    never_expires: false,
    is_active: true,
    applies_to_modules: ['shop'] as ModuleType[] // Par défaut uniquement shop
  });

  // Mettre à jour les modules par défaut quand availableModules change (pour nouveau coupon)
  useEffect(() => {
    if (!isEditing) {
      setFormData(prev => ({
        ...prev,
        applies_to_modules: defaultModules
      }));
    }
  }, [defaultModules, isEditing]);

  useEffect(() => {
    if (isEditing) {
      loadCoupon();
    }
  }, [id]);

  const loadCoupon = async () => {
    try {
      setLoading(true);
      const coupon = await getCouponById(id!);
      if (coupon) {
        const isNeverExpires = coupon.valid_until.startsWith(NEVER_EXPIRES_DATE);

        setFormData({
          code: coupon.code,
          name: coupon.name,
          description: coupon.description || '',
          discount_type: coupon.discount_type,
          discount_value: coupon.discount_value,
          min_purchase_amount: coupon.min_purchase_amount,
          max_discount_amount: coupon.max_discount_amount,
          usage_limit: coupon.usage_limit,
          usage_limit_per_user: coupon.usage_limit_per_user,
          valid_from: coupon.valid_from.split('T')[0],
          valid_until: coupon.valid_until.split('T')[0],
          never_expires: isNeverExpires,
          is_active: coupon.is_active,
          applies_to_modules: coupon.applies_to_modules
        });
      } else {
        toast.error('Coupon non trouvé');
        navigate('/admin/coupons');
      }
    } catch (error) {
      console.error('Erreur chargement coupon:', error);
      toast.error('Erreur lors du chargement du coupon');
      navigate('/admin/coupons');
    } finally {
      setLoading(false);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.code.trim()) {
      newErrors.code = 'Le code est requis';
    } else if (!/^[A-Z0-9_-]+$/i.test(formData.code)) {
      newErrors.code = 'Le code ne peut contenir que des lettres, chiffres, tirets et underscores';
    }

    if (!formData.name.trim()) {
      newErrors.name = 'Le nom est requis';
    }

    if (formData.discount_value <= 0) {
      newErrors.discount_value = 'La valeur de réduction doit être positive';
    }

    if (formData.discount_type === 'percentage' && formData.discount_value > 100) {
      newErrors.discount_value = 'Le pourcentage ne peut pas dépasser 100%';
    }

    if (!formData.never_expires) {
      if (!formData.valid_until) {
        newErrors.valid_until = 'La date de fin est requise';
      } else if (new Date(formData.valid_until) <= new Date(formData.valid_from)) {
        newErrors.valid_until = 'La date de fin doit être après la date de début';
      }
    }

    if (formData.applies_to_modules.length === 0) {
      newErrors.applies_to_modules = 'Sélectionnez au moins un module';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Veuillez corriger les erreurs du formulaire');
      return;
    }

    try {
      setSaving(true);

      const couponData = {
        code: formData.code.toUpperCase().trim(),
        name: formData.name.trim(),
        description: formData.description.trim() || null,
        discount_type: formData.discount_type,
        discount_value: formData.discount_value,
        min_purchase_amount: formData.min_purchase_amount,
        max_discount_amount: formData.max_discount_amount,
        usage_limit: formData.usage_limit,
        usage_limit_per_user: formData.usage_limit_per_user,
        valid_from: new Date(formData.valid_from).toISOString(),
        valid_until: new Date(
          (formData.never_expires ? NEVER_EXPIRES_DATE : formData.valid_until) + 'T23:59:59'
        ).toISOString(),
        is_active: formData.is_active,
        applies_to_modules: formData.applies_to_modules,
        created_by: null
      };

      if (isEditing) {
        await updateCoupon(id!, couponData);
        toast.success('Coupon mis à jour avec succès');
      } else {
        await createCoupon(couponData);
        toast.success('Coupon créé avec succès');
      }

      navigate('/admin/coupons');
    } catch (error: any) {
      console.error('Erreur sauvegarde:', error);
      if (error.message?.includes('duplicate') || error.code === '23505') {
        toast.error('Ce code de coupon existe déjà');
        setErrors({ code: 'Ce code existe déjà' });
      } else {
        toast.error('Erreur lors de la sauvegarde');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleModuleToggle = (module: ModuleType) => {
    setFormData(prev => ({
      ...prev,
      applies_to_modules: prev.applies_to_modules.includes(module)
        ? prev.applies_to_modules.filter(m => m !== module)
        : [...prev.applies_to_modules, module]
    }));
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-4 md:p-6 max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="mb-2">
        <button
          onClick={() => navigate('/admin/coupons')}
          className="flex items-center gap-2 text-gray-400 hover:text-white mb-4 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Retour à la liste
        </button>
        <h1 className="text-2xl font-bold text-white flex items-center gap-2">
          <Tag className="w-7 h-7 text-[#D4AF37]" />
          {isEditing ? 'Modifier le coupon' : 'Nouveau coupon'}
        </h1>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Informations de base */}
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Info className="w-5 h-5 text-[#D4AF37]" />
            Informations de base
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Code du coupon *
              </label>
              <input
                type="text"
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                placeholder="Ex: SUMMER20"
                className={`w-full px-4 py-2 bg-white/5 border rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#D4AF37] ${
                  errors.code ? 'border-red-500' : 'border-white/10'
                }`}
              />
              {errors.code && <p className="text-red-400 text-sm mt-1">{errors.code}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Nom du coupon *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Ex: Promo été 2025"
                className={`w-full px-4 py-2 bg-white/5 border rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#D4AF37] ${
                  errors.name ? 'border-red-500' : 'border-white/10'
                }`}
              />
              {errors.name && <p className="text-red-400 text-sm mt-1">{errors.name}</p>}
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Description (optionnel)
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Description interne du coupon..."
                rows={2}
                className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#D4AF37]"
              />
            </div>
          </div>
        </div>

        {/* Réduction */}
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Percent className="w-5 h-5 text-[#D4AF37]" />
            Réduction
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Type de réduction *
              </label>
              <select
                value={formData.discount_type}
                onChange={(e) => setFormData({ ...formData, discount_type: e.target.value as DiscountType })}
                className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#D4AF37]"
              >
                <option value="percentage">Pourcentage (%)</option>
                <option value="fixed_amount">Montant fixe (FCFA)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Valeur de la réduction *
              </label>
              <div className="relative">
                <input
                  type="number"
                  value={formData.discount_value}
                  onChange={(e) => setFormData({ ...formData, discount_value: parseFloat(e.target.value) || 0 })}
                  min="0"
                  step={formData.discount_type === 'percentage' ? '1' : '100'}
                  className={`w-full px-4 py-2 bg-white/5 border rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#D4AF37] ${
                    errors.discount_value ? 'border-red-500' : 'border-white/10'
                  }`}
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                  {formData.discount_type === 'percentage' ? '%' : 'FCFA'}
                </span>
              </div>
              {errors.discount_value && <p className="text-red-400 text-sm mt-1">{errors.discount_value}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Montant minimum d'achat
              </label>
              <div className="relative">
                <input
                  type="number"
                  value={formData.min_purchase_amount}
                  onChange={(e) => setFormData({ ...formData, min_purchase_amount: parseFloat(e.target.value) || 0 })}
                  min="0"
                  step="100"
                  className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#D4AF37]"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">FCFA</span>
              </div>
              <p className="text-gray-500 text-xs mt-1">0 = pas de minimum</p>
            </div>

            {formData.discount_type === 'percentage' && (
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Plafond de réduction
                </label>
                <div className="relative">
                  <input
                    type="number"
                    value={formData.max_discount_amount || ''}
                    onChange={(e) => setFormData({ ...formData, max_discount_amount: e.target.value ? parseFloat(e.target.value) : null })}
                    min="0"
                    step="100"
                    placeholder="Illimité"
                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#D4AF37]"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">FCFA</span>
                </div>
                <p className="text-gray-500 text-xs mt-1">Vide = pas de plafond</p>
              </div>
            )}
          </div>
        </div>

        {/* Validité */}
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-[#D4AF37]" />
            Période de validité
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Date de début *
              </label>
              <input
                type="date"
                value={formData.valid_from}
                onChange={(e) => setFormData({ ...formData, valid_from: e.target.value })}
                className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#D4AF37]"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Date de fin *
              </label>
              <input
                type="date"
                value={formData.never_expires ? NEVER_EXPIRES_DATE : formData.valid_until}
                onChange={(e) => setFormData({ ...formData, valid_until: e.target.value })}
                min={formData.valid_from}
                disabled={formData.never_expires}
                className={`w-full px-4 py-2 bg-white/5 border rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#D4AF37] ${
                  errors.valid_until ? 'border-red-500' : 'border-white/10'
                } ${formData.never_expires ? 'opacity-60 cursor-not-allowed' : ''}`}
              />
              {errors.valid_until && !formData.never_expires && (
                <p className="text-red-400 text-sm mt-1">{errors.valid_until}</p>
              )}
            </div>
          </div>

          <div className="mt-4 flex items-center gap-2">
            <input
              id="never_expires"
              type="checkbox"
              checked={formData.never_expires}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  never_expires: e.target.checked,
                  valid_until: e.target.checked && !formData.valid_until
                    ? NEVER_EXPIRES_DATE
                    : formData.valid_until
                })
              }
              className="w-4 h-4 rounded border-white/20 bg-white/5"
            />
            <label htmlFor="never_expires" className="text-sm text-gray-300">
              Ce coupon n'expire jamais (date de fin fixée à 31/12/2099)
            </label>
          </div>
        </div>

        {/* Limites d'utilisation */}
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Users className="w-5 h-5 text-[#D4AF37]" />
            Limites d'utilisation
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Limite globale d'utilisation
              </label>
              <input
                type="number"
                value={formData.usage_limit || ''}
                onChange={(e) => setFormData({ ...formData, usage_limit: e.target.value ? parseInt(e.target.value) : null })}
                min="1"
                placeholder="Illimité"
                className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#D4AF37]"
              />
              <p className="text-gray-500 text-xs mt-1">Vide = illimité</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Limite par utilisateur
              </label>
              <input
                type="number"
                value={formData.usage_limit_per_user}
                onChange={(e) => setFormData({ ...formData, usage_limit_per_user: parseInt(e.target.value) || 1 })}
                min="1"
                className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#D4AF37]"
              />
              <p className="text-gray-500 text-xs mt-1">Nombre de fois qu'un même utilisateur peut utiliser ce coupon</p>
            </div>
          </div>
        </div>

        {/* Modules */}
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
          <h2 className="text-lg font-semibold text-white mb-4">
            Modules autorisés *
          </h2>
          <p className="text-gray-400 text-sm mb-4">
            Sélectionnez les modules où ce coupon peut être utilisé
          </p>
          
          {availableModules.length === 1 && (
            <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3 mb-4">
              <p className="text-sm text-blue-200 flex items-center gap-2">
                <Info className="w-4 h-4" />
                Seule la Boutique est disponible. Activez d'autres modules dans Configuration &gt; Modules.
              </p>
            </div>
          )}
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {availableModules.map((module) => {
              const Icon = module.icon;
              const isSelected = formData.applies_to_modules.includes(module.value);
              return (
                <button
                  key={module.value}
                  type="button"
                  onClick={() => handleModuleToggle(module.value)}
                  className={`flex items-center gap-3 p-4 rounded-xl border-2 transition-all ${
                    isSelected
                      ? 'border-[#D4AF37] bg-[#D4AF37]/10'
                      : 'border-white/10 hover:border-white/30'
                  }`}
                >
                  <Icon className={`w-6 h-6 ${isSelected ? 'text-[#D4AF37]' : module.color}`} />
                  <span className={`font-medium ${isSelected ? 'text-white' : 'text-gray-300'}`}>
                    {module.label}
                  </span>
                </button>
              );
            })}
          </div>
          {errors.applies_to_modules && (
            <p className="text-red-400 text-sm mt-2 flex items-center gap-1">
              <AlertCircle className="w-4 h-4" />
              {errors.applies_to_modules}
            </p>
          )}
        </div>

        {/* Statut */}
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-white">Statut du coupon</h2>
              <p className="text-gray-400 text-sm">Activez ou désactivez ce coupon</p>
            </div>
            <button
              type="button"
              onClick={() => setFormData({ ...formData, is_active: !formData.is_active })}
              className={`relative w-14 h-7 rounded-full transition-colors ${
                formData.is_active ? 'bg-green-500' : 'bg-gray-600'
              }`}
            >
              <span
                className={`absolute top-1 w-5 h-5 bg-white rounded-full transition-transform ${
                  formData.is_active ? 'left-8' : 'left-1'
                }`}
              />
            </button>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-4">
          <button
            type="button"
            onClick={() => navigate('/admin/coupons')}
            className="px-6 py-2 border border-white/10 text-gray-300 rounded-lg hover:bg-white/5 transition-colors"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={saving}
            className="flex items-center gap-2 px-6 py-2 bg-gradient-to-r from-[#D4AF37] to-[#B8941F] text-white rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Enregistrement...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                {isEditing ? 'Mettre à jour' : 'Créer le coupon'}
              </>
            )}
          </button>
        </div>
      </form>
      </div>
    </AdminLayout>
  );
}
